import React, { useMemo, useState } from 'react';
import { Button, Steps, Tooltip } from 'antd';
import './ChangeRequestSteps.less';
import ChangeRequestAnalysis from '../ChangeRequestAnalysis/ChangeRequestAnalysis';
import ChangeRequestActions from '../ChangeRequestActions/ChangeRequestActions';
import ChangeRequestSummary from '../ChangeRequestSummary/ChangeRequestSummary';
import { RootState } from '../../../app/store';
import { useSelector } from 'react-redux';
import {
  useAgentSelector,
  useAgentDispatch,
} from '../../../hooks/useAgentStore';
import { updateCurrentStep } from '../../../app/Slices/changeRequestStepsSlice';
import { setCREditingDialogExpanded } from '../../../app/Slices/changeRequestEditSlice';
import { Notify } from '../../../services/notificationService';
import * as subscriptionService from '../../../services/subscription';
import { isOwnChangeRequest } from '../../../services/changeRequestService';
import { NOT_OWN_CHANGE_REQUEST } from '../../../constants/texts';
import { selectSubscriptionStatus } from '../../../app/Slices/subscriptionSlice';

const ChangeRequestSteps: React.FC = () => {
  const agentDispatch = useAgentDispatch();

  // Global store for shared state
  const org = useSelector((state: RootState) => state.organization.org);
  const status = useSelector(selectSubscriptionStatus);

  // Agent store for change request state
  const { data, loading } = useAgentSelector((state) => state.changeRequest);
  const cr = useMemo(() => {
    return data?.result;
  }, [data?.result]);

  const [currentStep, setCurrentStep] = useState(0);
  const isUserReadonly = subscriptionService.isUserReadonly(status);
  const isCrReadOnly = !cr || !isOwnChangeRequest(org, cr);
  const isReadonly = isCrReadOnly || isUserReadonly;
  const readonlyMsg = isCrReadOnly
    ? NOT_OWN_CHANGE_REQUEST
    : subscriptionService.getReadonlyElementTitle();

  // Step titles and contents
  const steps = [
    {
      title: 'Analysis',
      content: () => <ChangeRequestAnalysis />,
      nextLabel: 'Next step: Action Items >',
    },
    {
      title: 'Actions',
      content: () => <ChangeRequestActions />,
      nextLabel: 'Next step: Summary >',
      prevLabel: '< Step back: Request Analysis',
      disabled: !cr?.checklist?.items?.length,
    },
    {
      title: 'Summary',
      content: () => <ChangeRequestSummary />,
      prevLabel: '< Step back: Action Items',
      disabled: !cr?.execution?.items?.length,
    },
  ];

  const updateNavigation = (step: number) => {
    setCurrentStep(step);
    agentDispatch(updateCurrentStep(step));
    agentDispatch(setCREditingDialogExpanded(false));
  };

  // Handlers for navigation
  const next = () => updateNavigation(currentStep + 1);
  const prev = () => updateNavigation(currentStep - 1);
  const onChange = (step: number) => updateNavigation(step);

  const items = steps.map((item) => ({
    key: item.title,
    title: item.title,
    disabled: item.disabled,
  }));

  return (
    <div id="change-request-details" data-testid="change-request-details">
      {Boolean(cr?.checklist?.items?.length) && !loading && (
        <div className="top-nav-btn cr-sticky-element">
          {currentStep > 0 && (
            <Button onClick={prev}>{steps[currentStep].prevLabel}</Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button onClick={next}>{steps[currentStep].nextLabel}</Button>
          )}
          {currentStep === steps.length - 1 && (
            <Tooltip
              title={readonlyMsg}
              placement="right"
              destroyOnHidden={false}
            >
              <Button
                onClick={() => Notify.success('Change Request completed!')}
                disabled={isReadonly}
              >
                Done
              </Button>
            </Tooltip>
          )}
        </div>
      )}
      <Steps
        size="small"
        current={currentStep}
        items={items}
        className="steps"
        onChange={onChange}
      />

      <div className="wrapper" style={{ marginTop: 16 }}>
        {steps[currentStep].content()}
      </div>

      {Boolean(cr?.checklist?.items?.length) && !loading && (
        <div className="footer">
          {currentStep > 0 && (
            <Button onClick={prev}>{steps[currentStep].prevLabel}</Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button onClick={next}>{steps[currentStep].nextLabel}</Button>
          )}
          {currentStep === steps.length - 1 && (
            <Tooltip
              title={readonlyMsg}
              placement="right"
              destroyOnHidden={false}
            >
              <Button
                onClick={() => Notify.success('Change Request completed!')}
                disabled={isReadonly}
              >
                Done
              </Button>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};

export default ChangeRequestSteps;
